import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minimize2, X, Volume2, VolumeX } from "lucide-react";

type CoachSkillActivityEvent = {
  widgetType: "coping_buddy";
  widgetLabel: "Coping Buddy";
  activityKey: string;
  activityTitle: string;
  eventType: "started" | "completed" | "used" | "saved" | "submitted";
  metadata?: Record<string, unknown>;
};

type CoachSkillWidgetProps = {
  coachSkill: string | null | undefined;
  defaultAudioEnabled?: boolean;
  onActivityEvent?: (event: CoachSkillActivityEvent) => void;
};

export type CoachSkillVoiceCommand =
  | "start"
  | "pause"
  | "resume"
  | "reset";

export type CoachSkillWidgetHandle = {
  /**
   * Returns true only when the transcript is an exact, supported
   * Coach Skill command that should be consumed locally.
   */
  executeVoiceCommand: (rawTranscript: string) => boolean;
};

type CoachPhase = {
  key: string;
  label: string;
  instruction: string;
  seconds: number;
  audioUrl?: string;
};

type CoachSkillTheme = {
  panelBg: string;
  inactiveStroke: string;
  activeStroke: string;
};

type CoachSkillDefinition = {
  title: string;
  subtitle: string;
  phases: CoachPhase[];
  theme: CoachSkillTheme;
  kind: "box" | "hand" | "audio" | "video" | "html";
  audioUrl?: string;
  mediaUrl?: string;
  htmlUrl?: string;
  loop?: boolean;

  // Optional local realtime-voice controls.
  // Skills that omit this field do not intercept voice commands.
  voiceCommands?: readonly CoachSkillVoiceCommand[];

  // UI behavior
  showDurationInInstruction?: boolean; // e.g. "for a count of 4"
  footerMode?: "count" | "phaseLabel"; // number vs Inhale/Exhale
};

const BOX_BREATHING_PHASES: CoachPhase[] = [
  {
    key: "inhale",
    label: "Inhale",
    instruction: "Inhale slowly through your nose",
    seconds: 4,
  },
  {
    key: "hold_top",
    label: "Hold",
    instruction: "Hold your breath gently",
    seconds: 4,
  },
  {
    key: "exhale",
    label: "Exhale",
    instruction: "Exhale slowly through your mouth",
    seconds: 4,
  },
  {
    key: "hold_bottom",
    label: "Hold",
    instruction: "Hold your breath gently",
    seconds: 4,
  },
];

const HILLS_AND_VALLEYS_PHASES: CoachPhase[] = [
  {
    key: "thumb_up",
    label: "Inhale",
    instruction: "Trace up the thumb",
    seconds: 4,
    audioUrl: "/assets/coach-skills/hills-and-valleys/inhale.mp3",
  },
  {
    key: "thumb_down",
    label: "Exhale",
    instruction: "Trace down into the valley",
    seconds: 4,
    audioUrl: "/assets/coach-skills/hills-and-valleys/exhale.mp3",
  },
  {
    key: "index_up",
    label: "Inhale",
    instruction: "Trace up the index finger",
    seconds: 4,
    audioUrl: "/assets/coach-skills/hills-and-valleys/inhale.mp3",
  },
  {
    key: "index_down",
    label: "Exhale",
    instruction: "Trace down into the valley",
    seconds: 4,
    audioUrl: "/assets/coach-skills/hills-and-valleys/exhale.mp3",
  },
  {
    key: "middle_up",
    label: "Inhale",
    instruction: "Trace up the middle finger",
    seconds: 4,
    audioUrl: "/assets/coach-skills/hills-and-valleys/inhale.mp3",
  },
  {
    key: "middle_down",
    label: "Exhale",
    instruction: "Trace down into the valley",
    seconds: 4,
    audioUrl: "/assets/coach-skills/hills-and-valleys/exhale.mp3",
  },
  {
    key: "ring_up",
    label: "Inhale",
    instruction: "Trace up the ring finger",
    seconds: 4,
    audioUrl: "/assets/coach-skills/hills-and-valleys/inhale.mp3",
  },
  {
    key: "ring_down",
    label: "Exhale",
    instruction: "Trace down into the valley",
    seconds: 4,
    audioUrl: "/assets/coach-skills/hills-and-valleys/exhale.mp3",
  },
  {
    key: "pinky_up",
    label: "Inhale",
    instruction: "Trace up the pinky",
    seconds: 4,
    audioUrl: "/assets/coach-skills/hills-and-valleys/inhale.mp3",
  },
  {
    key: "pinky_down",
    label: "Exhale",
    instruction: "Trace down the outer edge",
    seconds: 4,
    audioUrl: "/assets/coach-skills/hills-and-valleys/exhale.mp3",
  },
];

const COACH_SKILL_DEFS: Record<string, CoachSkillDefinition> = {
  "box breathing": {
    title: "Box Breathing",
    subtitle: "Trace the box as you breathe",
    phases: BOX_BREATHING_PHASES,
    kind: "box",
    audioUrl: "/assets/coach-skills/box-breathing/box-breathing.m4a",
    voiceCommands: ["start", "pause", "resume", "reset"],
    theme: {
      panelBg: "#F4F8FF",
      inactiveStroke: "#D6E4FF",
      activeStroke: "#3B82F6",
    },
  },
  "hills and valleys breathing": {
    title: "Hills and Valleys Breathing",
    subtitle: "Trace the hand contour as you breathe",
    phases: HILLS_AND_VALLEYS_PHASES,
    kind: "hand",
    voiceCommands: ["start", "pause", "resume", "reset"],
    showDurationInInstruction: false,
    footerMode: "phaseLabel",
    theme: {
      panelBg: "#F4F8FF",
      inactiveStroke: "#D6E4FF",
      activeStroke: "#3B82F6",
    },
  },
};

function normalizeSkillKey(v: string | null | undefined): string {
  return String(v ?? "").trim().toLowerCase();
}

function parseCoachSkillVoiceCommand(
  rawTranscript: string
): CoachSkillVoiceCommand | null {
  const normalized = String(rawTranscript ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Intentionally exact. Do not treat phrases such as:
  // "I'm done", "next", "stop", or "what do I do now"
  // as local widget commands.
  if (normalized === "start") return "start";
  if (normalized === "pause") return "pause";
  if (normalized === "resume") return "resume";
  if (normalized === "reset") return "reset";

  return null;
}

function getCoachSkillDefinition(
  coachSkill: string | null | undefined
): CoachSkillDefinition | null {
  const key = normalizeSkillKey(coachSkill);
  return COACH_SKILL_DEFS[key] ?? null;
}

function BoxVisualizer({
  stepIndex,
  progressInStep,
  theme,
}: {
  stepIndex: number;
  progressInStep: number;
  theme: CoachSkillTheme;
}) {
  const box = {
    left: 70,
    top: 180,
    size: 180,
  };

  const bottomLeft = { x: box.left, y: box.top + box.size };
  const topLeft = { x: box.left, y: box.top };
  const topRight = { x: box.left + box.size, y: box.top };
  const bottomRight = { x: box.left + box.size, y: box.top + box.size };

  const pathD = [
    `M ${bottomLeft.x} ${bottomLeft.y}`,
    `L ${topLeft.x} ${topLeft.y}`,
    `L ${topRight.x} ${topRight.y}`,
    `L ${bottomRight.x} ${bottomRight.y}`,
    `L ${bottomLeft.x} ${bottomLeft.y}`,
  ].join(" ");

  const segment1 = `M ${bottomLeft.x} ${bottomLeft.y} L ${topLeft.x} ${topLeft.y}`;
  const segment2 = `M ${topLeft.x} ${topLeft.y} L ${topRight.x} ${topRight.y}`;
  const segment3 = `M ${topRight.x} ${topRight.y} L ${bottomRight.x} ${bottomRight.y}`;
  const segment4 = `M ${bottomRight.x} ${bottomRight.y} L ${bottomLeft.x} ${bottomLeft.y}`;
  const segments = [segment1, segment2, segment3, segment4];

  const segmentLength = box.size;
  const dashOffset = segmentLength * (1 - progressInStep);

  const getDotPosition = () => {
    switch (stepIndex) {
      case 0:
        return {
          x: box.left,
          y: box.top + box.size * (1 - progressInStep),
        };
      case 1:
        return {
          x: box.left + box.size * progressInStep,
          y: box.top,
        };
      case 2:
        return {
          x: box.left + box.size,
          y: box.top + box.size * progressInStep,
        };
      case 3:
        return {
          x: box.left + box.size * (1 - progressInStep),
          y: box.top + box.size,
        };
      default:
        return bottomLeft;
    }
  };

  const dot = getDotPosition();

  return (
    <div className="mt-1 flex items-center justify-center">
      <svg
        viewBox="0 135 320 200"
        className="w-full max-w-[220px] overflow-visible sm:max-w-[260px]"
      >
        <path
          d={pathD}
          fill="none"
          stroke={theme.inactiveStroke}
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {stepIndex > 0 && (
          <path
            d={segment1}
            fill="none"
            stroke={theme.activeStroke}
            strokeWidth="12"
            strokeLinecap="round"
          />
        )}
        {stepIndex > 1 && (
          <path
            d={segment2}
            fill="none"
            stroke={theme.activeStroke}
            strokeWidth="12"
            strokeLinecap="round"
          />
        )}
        {stepIndex > 2 && (
          <path
            d={segment3}
            fill="none"
            stroke={theme.activeStroke}
            strokeWidth="12"
            strokeLinecap="round"
          />
        )}

        <path
          d={segments[Math.min(stepIndex, segments.length - 1)]}
          fill="none"
          stroke={theme.activeStroke}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={segmentLength}
          strokeDashoffset={dashOffset}
        />

        <circle cx={dot.x} cy={dot.y} r="10" fill={theme.activeStroke} />
      </svg>
    </div>
  );
}

function HandVisualizer({
  stepIndex,
  progressInStep,
  theme,
}: {
  stepIndex: number;
  progressInStep: number;
  theme: CoachSkillTheme;
}) {
  const [dot, setDot] = React.useState({ x: 78, y: 330 });
  const activePathRef = React.useRef<SVGPathElement | null>(null);

  const handFillPath = [
    `M 78 322`,
    `Q 58 302, 48 278`,
    `Q 34 250, 22 232`,
    `Q 8 212, 18 198`,
    `Q 28 184, 48 188`,
    `Q 64 192, 72 208`,
    `L 96 260`,
    `L 96 126`,
    `Q 98 92, 118 92`,
    `Q 138 92, 140 126`,
    `L 146 248`,
    `L 158 84`,
    `Q 162 44, 182 44`,
    `Q 202 44, 206 84`,
    `L 214 248`,
    `L 224 118`,
    `Q 228 82, 248 82`,
    `Q 268 82, 270 118`,
    `L 278 254`,
    `L 300 182`,
    `Q 312 144, 334 150`,
    `Q 350 156, 346 184`,
    `Q 342 218, 330 244`,
    `Q 320 270, 318 294`,
    `Q 314 324, 292 336`,
    `Q 264 348, 216 346`,
    `L 144 340`,
    `Q 100 336, 78 322`,
    `Z`,
  ].join(" ");

  // V7: only the thumb segments differ; fingers 2–5 stay unchanged.
  const contourSegments = [
    // Thumb up only: lower start, gradual rise, stop near tip.
    `M 78 330 Q 66 316 54 296 Q 42 272 32 246 Q 24 226 22 208 Q 22 194 30 186 Q 40 180 54 182 Q 66 188 72 208`,
    // Thumb down only: begin at tip and descend into valley 1.
    `M 72 208 Q 80 216 86 230 Q 92 244 96 260`,
    `M 96 260 L 96 126 Q 98 92 118 92`,
    `M 118 92 Q 138 92 140 126 L 146 248 Q 150 266 158 286`,
    `M 158 286 L 158 84 Q 162 44 182 44`,
    `M 182 44 Q 202 44 206 84 L 214 248 Q 218 266 220 286`,
    `M 220 286 L 224 118 Q 228 82 248 82`,
    `M 248 82 Q 268 82 270 118 L 278 254 Q 280 270 282 286`,
    `M 282 286 L 300 182 Q 312 144 334 150`,
    `M 334 150 Q 350 156 346 184 Q 342 218 330 244 Q 320 270 318 294 Q 314 324 292 336 Q 286 338 278 318`,
  ];

  const defaultDotStarts = [
    { x: 78, y: 330 },
    { x: 72, y: 208 },
    { x: 96, y: 260 },
    { x: 118, y: 92 },
    { x: 158, y: 286 },
    { x: 182, y: 44 },
    { x: 220, y: 286 },
    { x: 248, y: 82 },
    { x: 282, y: 286 },
    { x: 334, y: 150 },
  ];

  const safeIndex = Math.min(stepIndex, contourSegments.length - 1);
  const currentSegment = contourSegments[safeIndex];

  React.useEffect(() => {
    setDot(defaultDotStarts[safeIndex] ?? defaultDotStarts[0]);
  }, [safeIndex]);

  React.useEffect(() => {
    const path = activePathRef.current;
    if (!path) return;

    try {
      const total = path.getTotalLength();
      const point = path.getPointAtLength(total * progressInStep);
      setDot({ x: point.x, y: point.y });
    } catch {
      // no-op
    }
  }, [safeIndex, progressInStep]);

  return (
    <div className="mt-1 flex items-center justify-center">
      <svg
        viewBox="10 35 345 315"
        className="w-full max-w-[220px] overflow-visible sm:max-w-[250px]"
      >
        <path
          d={handFillPath}
          fill="#F2D2AE"
          stroke="#111827"
          strokeWidth="8"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {contourSegments.map((seg, idx) => (
          <path
            key={`base-${idx}`}
            d={seg}
            fill="none"
            stroke={theme.inactiveStroke}
            strokeWidth="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {contourSegments.slice(0, safeIndex).map((seg, idx) => (
          <path
            key={`done-${idx}`}
            d={seg}
            fill="none"
            stroke={theme.activeStroke}
            strokeWidth="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        <path
          ref={activePathRef}
          d={currentSegment}
          fill="none"
          stroke={theme.activeStroke}
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength={100}
          strokeDasharray="100"
          strokeDashoffset={100 - progressInStep * 100}
        />

        <circle cx={dot.x} cy={dot.y} r="9" fill={theme.activeStroke} />
      </svg>
    </div>
  );
}

const CoachSkillWidget = forwardRef<
  CoachSkillWidgetHandle,
  CoachSkillWidgetProps
>(function CoachSkillWidget(
  {
    coachSkill,
    defaultAudioEnabled = true,
    onActivityEvent,
  },
  ref
) {
  const def = useMemo(() => getCoachSkillDefinition(coachSkill), [coachSkill]);

  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(def?.phases[0]?.seconds ?? 0);
  const [progressInStep, setProgressInStep] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(defaultAudioEnabled);
  const [isHtmlExpanded, setIsHtmlExpanded] = useState(false);
  const [isCoachExpanded, setIsCoachExpanded] = useState(false);

  useEffect(() => {
    setAudioEnabled(defaultAudioEnabled);
  }, [defaultAudioEnabled]);

  const tickRef = useRef<number | null>(null);
  const endAtRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Preserve the exact visual and audio position during Pause/Resume.
  const progressRef = useRef(0);
  const isResumingRef = useRef(false);

  const startedEventSentRef = useRef(false);
  const completedEventSentRef = useRef(false);

  const activityKey = useMemo(() => {
    return normalizeSkillKey(coachSkill)
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
  }, [coachSkill]);

  const emitActivityEvent = useCallback(
    (
      eventType: CoachSkillActivityEvent["eventType"],
      metadata?: Record<string, unknown>
    ) => {
      if (!def || !activityKey) return;

      onActivityEvent?.({
        widgetType: "coping_buddy",
        widgetLabel: "Coping Buddy",
        activityKey,
        activityTitle: def.title,
        eventType,
        metadata,
      });
    },
    [activityKey, def, onActivityEvent]
  );

  useEffect(() => {
    if (!def || def.kind !== "html") return;

    const handleMessage = (event: MessageEvent) => {
      // Same-origin activities under /activities should come from the app origin.
      if (event.origin !== window.location.origin) return;

      const msg = event.data;

      if (!msg || typeof msg !== "object") return;
      if (msg.source !== "efa-activity") return;
      if (msg.type !== "ACTIVITY_EVENT") return;

      const rawEventType = String(msg.eventType ?? "").toLowerCase().trim();

      const eventTypeMap: Record<string, CoachSkillActivityEvent["eventType"]> = {
        open: "started",
        opened: "started",
        start: "started",
        started: "started",
        complete: "completed",
        completed: "completed",
        first_action: "used",
        used: "used",
        save: "saved",
        saved: "saved",
        submit: "submitted",
        submitted: "submitted",
      };

      const eventType = eventTypeMap[rawEventType];
      if (!eventType) return;

      emitActivityEvent(eventType, {
        renderType: "htmlActivity",
        sourceActivityKey:
          typeof msg.activityKey === "string" ? msg.activityKey : activityKey,
        sourceActivityTitle:
          typeof msg.activityTitle === "string" ? msg.activityTitle : def.title,
        ...(msg.metadata && typeof msg.metadata === "object" ? msg.metadata : {}),
      });
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [activityKey, def, emitActivityEvent]);

  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.preload = "auto";

    return () => {
      if (tickRef.current) {
        window.clearTimeout(tickRef.current);
        tickRef.current = null;
      }

      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.loop = false;
          audioRef.current.src = "";
        } catch { }
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!def) return;

    startedEventSentRef.current = false;
    completedEventSentRef.current = false;

    setIsHtmlExpanded(false);
    setIsCoachExpanded(false);

    if (tickRef.current) {
      window.clearTimeout(tickRef.current);
      tickRef.current = null;
    }

    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.loop = false;
        audioRef.current.src = "";
      } catch { }
    }

    endAtRef.current = null;
    progressRef.current = 0;
    isResumingRef.current = false;

    setIsRunning(false);
    setStepIndex(0);
    setSecondsLeft(def.phases[0]?.seconds ?? 0);
    setProgressInStep(0);
  }, [def]);

  useEffect(() => {
    if (!isCoachExpanded && !isHtmlExpanded) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsCoachExpanded(false);
        setIsHtmlExpanded(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isCoachExpanded, isHtmlExpanded]);

  useEffect(() => {
    if (!def || !isRunning) return;

    const currentStep = def.phases[stepIndex];
    const totalMs = currentStep.seconds * 1000;

    // Resume from the exact preserved position within the current phase.
    const startingProgress = Math.max(
      0,
      Math.min(1, progressRef.current)
    );

    const remainingDurationMs = Math.max(
      1,
      totalMs * (1 - startingProgress)
    );

    endAtRef.current = Date.now() + remainingDurationMs;

    const runTick = () => {
      const now = Date.now();
      const endAt = endAtRef.current ?? now;
      const msLeft = Math.max(0, endAt - now);

      const resumedSegmentProgress = Math.max(
        0,
        Math.min(1, 1 - msLeft / remainingDurationMs)
      );

      const nextProgress = Math.max(
        0,
        Math.min(
          1,
          startingProgress +
          (1 - startingProgress) * resumedSegmentProgress
        )
      );

      const nextSeconds = Math.max(0, Math.ceil(msLeft / 1000));

      progressRef.current = nextProgress;
      setProgressInStep(nextProgress);
      setSecondsLeft(nextSeconds);

      if (msLeft <= 0) {
        const nextIndex = stepIndex + 1;

        if (nextIndex >= def.phases.length) {
          if (!completedEventSentRef.current) {
            completedEventSentRef.current = true;

            emitActivityEvent("completed", {
              renderType: "coachSkill",
              phaseCount: def.phases.length,
              totalDurationSeconds: def.phases.reduce(
                (sum, phase) => sum + phase.seconds,
                0
              ),
            });
          }

          if (def.loop) {
            progressRef.current = 0;
            setStepIndex(0);
            setSecondsLeft(def.phases[0].seconds);
            setProgressInStep(0);
            endAtRef.current = null;
            tickRef.current = null;
            return;
          }
          
          // Stop the guided audio when the final breathing phase finishes.
          if (audioRef.current) {
            try {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
              audioRef.current.loop = false;
            } catch { }
          }
          
          progressRef.current = 0;
          setIsRunning(false);
          setStepIndex(0);
          setSecondsLeft(def.phases[0].seconds);
          setProgressInStep(0);
          endAtRef.current = null;
          tickRef.current = null;
          return;
        }

        progressRef.current = 0;
        setStepIndex(nextIndex);
        setSecondsLeft(def.phases[nextIndex].seconds);
        setProgressInStep(0);
        endAtRef.current = null;
        tickRef.current = null;
        return;
      }

      tickRef.current = window.setTimeout(runTick, 80);
    };

    tickRef.current = window.setTimeout(runTick, 0);

    return () => {
      if (tickRef.current) {
        window.clearTimeout(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [def, emitActivityEvent, isRunning, stepIndex]);

  useEffect(() => {
    if (!def || !isRunning || !audioEnabled) return;
    if (def.kind === "box") return;

    // On Resume, continue the existing phase audio instead of restarting it.
    if (isResumingRef.current) {
      isResumingRef.current = false;
      return;
    }

    const currentStep = def.phases[stepIndex];
    const audioUrl = currentStep?.audioUrl;
    if (!audioUrl) return;
    if (!audioRef.current) return;

    const el = audioRef.current;

    const playPhaseAudio = async () => {
      try {
        el.pause();
        el.currentTime = 0;
        el.loop = false;
        el.src = audioUrl;
        await el.play();
      } catch (err) {
        console.warn("[CoachSkillWidget] phase audio failed:", err);
      }
    };

    void playPhaseAudio();
  }, [def, isRunning, stepIndex, audioEnabled]);

  const steps = def?.phases ?? [];
  const firstStepSeconds = steps[0]?.seconds ?? 0;

  const isReadyToStart =
    Boolean(def) &&
    steps.length > 0 &&
    !isRunning &&
    stepIndex === 0 &&
    progressInStep === 0 &&
    secondsLeft === firstStepSeconds;

  const toggleAudio = useCallback(() => {
    setAudioEnabled((prev) => {
      const next = !prev;

      if (!next && audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        } catch { }
      }

      return next;
    });
  }, []);

  const start = useCallback(() => {
    if (!def || steps.length === 0) return;

    progressRef.current = 0;
    isResumingRef.current = false;
    endAtRef.current = null;

    setStepIndex(0);
    setSecondsLeft(steps[0].seconds);
    setProgressInStep(0);

    if (audioEnabled && audioRef.current) {
      const audioUrl = def.audioUrl ?? steps[0]?.audioUrl;

      if (audioUrl) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.loop = Boolean(def.loop);
          audioRef.current.src = audioUrl;

          void audioRef.current.play().catch((err) => {
            console.warn("[CoachSkillWidget] skill audio failed:", err);
          });
        } catch (err) {
          console.warn(
            "[CoachSkillWidget] skill audio setup failed:",
            err
          );
        }
      }
    }

    completedEventSentRef.current = false;

    emitActivityEvent("started", {
      renderType: "coachSkill",
      phaseCount: steps.length,
      totalDurationSeconds: steps.reduce(
        (sum, step) => sum + step.seconds,
        0
      ),
    });

    setIsRunning(true);
  }, [audioEnabled, def, emitActivityEvent, steps]);

  const reset = useCallback(() => {
    if (steps.length === 0) return;

    if (tickRef.current) {
      window.clearTimeout(tickRef.current);
      tickRef.current = null;
    }

    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.loop = false;
        audioRef.current.src = "";
      } catch { }
    }

    startedEventSentRef.current = false;
    completedEventSentRef.current = false;

    endAtRef.current = null;
    progressRef.current = 0;
    isResumingRef.current = false;

    setIsRunning(false);
    setStepIndex(0);
    setSecondsLeft(steps[0].seconds);
    setProgressInStep(0);
  }, [steps]);

  const pause = useCallback(() => {
    if (tickRef.current) {
      window.clearTimeout(tickRef.current);
      tickRef.current = null;
    }

    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch { }
    }

    setIsRunning(false);
  }, []);

  const resume = useCallback(() => {
    if (!def || steps.length === 0) return;

    isResumingRef.current = Boolean(
      audioEnabled &&
      audioRef.current &&
      def.kind !== "box"
    );

    if (audioEnabled && audioRef.current) {
      void audioRef.current.play().catch((err) => {
        console.warn("[CoachSkillWidget] resume audio failed:", err);
      });
    }

    setIsRunning(true);
  }, [audioEnabled, def, steps.length]);

  useImperativeHandle(
    ref,
    () => ({
      executeVoiceCommand(rawTranscript: string): boolean {
        const command = parseCoachSkillVoiceCommand(rawTranscript);

        // Not an exact widget command. Allow ChatContainer to send it
        // through the regular assistant/SUDS flow.
        if (!command) return false;

        // This particular skill does not support the recognized command.
        if (!def?.voiceCommands?.includes(command)) return false;

        // Only the animated phase-based widgets currently expose these
        // local controls.
        if (
          (def.kind !== "box" && def.kind !== "hand") ||
          steps.length === 0
        ) {
          return false;
        }

        // A recognized, supported command is consumed locally even when it
        // is not valid in the current state. This prevents an accidental
        // "Pause" before Start from being sent as an invalid SUDS response.
        if (command === "start") {
          if (isReadyToStart) start();
          return true;
        }

        if (command === "pause") {
          if (isRunning) pause();
          return true;
        }

        if (command === "resume") {
          if (!isRunning && !isReadyToStart) resume();
          return true;
        }

        if (command === "reset") {
          reset();
          return true;
        }

        return false;
      },
    }),
    [
      def,
      isReadyToStart,
      isRunning,
      pause,
      reset,
      resume,
      start,
      steps.length,
    ]
  );

  if (!def) return null;

  if (def.kind === "audio") {
    return (
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">{def.title}</div>
            <div className="text-xs text-slate-500">{def.subtitle}</div>
          </div>

          <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
            Audio practice
          </div>
        </div>

        <div
          className="mt-3 rounded-2xl p-4"
          style={{ backgroundColor: def.theme.panelBg }}
        >
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Guided Exercise
          </div>

          <div className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">
            Press play and follow along with the Leaves on a Stream practice.
          </div>

          <div className="mt-4 rounded-xl bg-white/80 p-3 shadow-sm">
            <audio
              controls
              preload="metadata"
              className="w-full"
              src={def.audioUrl}
            >
              Your browser does not support the audio player.
            </audio>
          </div>
        </div>
      </div>
    );
  }

  if (def.kind === "video") {
    return (
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">{def.title}</div>
            <div className="text-xs text-slate-500">{def.subtitle}</div>
          </div>

          <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
            Guided video
          </div>
        </div>

        <div
          className="mt-3 rounded-2xl p-4"
          style={{ backgroundColor: def.theme.panelBg }}
        >
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Guided Exercise
          </div>

          <div className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">
            Press play and follow along with the Leaves on a Stream practice.
          </div>

          <div className="mt-4 overflow-hidden rounded-xl bg-black shadow-sm">
            {def.mediaUrl ? (
              <video
                controls
                loop={def.loop ?? true}
                preload="metadata"
                playsInline
                className="w-full rounded-xl"
                src={def.mediaUrl}
                onLoadedMetadata={() => {
                  console.log(
                    "[CoachSkillWidget] Leaves video metadata loaded:",
                    def.mediaUrl
                  );
                }}
                onError={(event) => {
                  console.warn(
                    "[CoachSkillWidget] Leaves video failed:",
                    event.currentTarget.error
                  );
                }}
              >
                Your browser does not support the video player.
              </video>
            ) : (
              <div className="p-4 text-sm text-slate-600">
                Video unavailable.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (def.kind === "html") {
    return (
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">
              {def.title}
            </div>
            <div className="text-xs text-slate-500">{def.subtitle}</div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
              Guided activity
            </div>

            <button
              type="button"
              onClick={() => setIsHtmlExpanded(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-100"
              aria-label={`Maximize ${def.title}`}
            >
              <Maximize2 className="h-3.5 w-3.5" />
              Maximize
            </button>
          </div>
        </div>

        <div
          className="mt-3 overflow-hidden rounded-2xl p-2"
          style={{ backgroundColor: def.theme.panelBg }}
        >
          {def.htmlUrl ? (
            <iframe
              title={def.title}
              src={def.htmlUrl}
              className="h-[640px] w-full rounded-xl border-0 bg-white"
              loading="lazy"
            />
          ) : (
            <div className="rounded-xl bg-white p-4 text-sm text-slate-600">
              Activity unavailable.
            </div>
          )}
        </div>

        {isHtmlExpanded && def.htmlUrl ? (
          <div className="fixed inset-0 z-[1000] bg-slate-950/70 p-3 sm:p-5">
            <div className="flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {def.title}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {def.subtitle}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsHtmlExpanded(false)}
                    className="hidden items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-100 sm:inline-flex"
                    aria-label={`Exit maximized ${def.title}`}
                  >
                    <Minimize2 className="h-3.5 w-3.5" />
                    Minimize
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsHtmlExpanded(false)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                    aria-label="Close expanded activity"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <iframe
                title={`${def.title} expanded`}
                src={def.htmlUrl}
                className="min-h-0 flex-1 border-0 bg-white"
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  const currentStep = steps[stepIndex];
  const theme = def.theme;

  const isHillsAndValleys =
    normalizeSkillKey(coachSkill) === "hills and valleys breathing";

  const displayCount = Math.min(
    currentStep.seconds,
    Math.max(1, currentStep.seconds - secondsLeft + 1)
  );

  const instructionText = isHillsAndValleys
    ? currentStep.instruction
    : `${currentStep.instruction} for a count of ${currentStep.seconds}.`;

  const footerText = isHillsAndValleys
    ? currentStep.label
    : String(displayCount);

  const renderCoachSkillContent = () => (
    <>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">{def.title}</div>
          <div className="text-xs text-slate-500">{def.subtitle}</div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleAudio}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
            aria-pressed={!audioEnabled}
            aria-label={audioEnabled ? "Mute breathing audio" : "Unmute breathing audio"}
            title={audioEnabled ? "Mute breathing audio" : "Unmute breathing audio"}
          >
            {audioEnabled ? (
              <Volume2 className="h-4 w-4" />
            ) : (
              <VolumeX className="h-4 w-4" />
            )}
          </button>

          <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
            {def.loop ? "Looping" : `Step ${stepIndex + 1} of ${steps.length}`}
          </div>

          <button
            type="button"
            onClick={() => setIsCoachExpanded((prev) => !prev)}
            className="inline-flex h-8 items-center gap-1.5 rounded-full bg-slate-100 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-200"
            aria-label={isCoachExpanded ? `Minimize ${def.title}` : `Maximize ${def.title}`}
            title={isCoachExpanded ? "Minimize" : "Maximize"}
          >
            {isCoachExpanded ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
            {isCoachExpanded ? "Minimize" : "Maximize"}
          </button>
        </div>
      </div>

      <div
        className="mt-3 rounded-2xl p-3 sm:p-4"
        style={{ backgroundColor: theme.panelBg }}
      >
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {currentStep.label}
        </div>

        <div className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">
          {instructionText}
        </div>

        {def.kind === "box" ? (
          <BoxVisualizer
            stepIndex={stepIndex}
            progressInStep={progressInStep}
            theme={theme}
          />
        ) : (
          <HandVisualizer
            stepIndex={stepIndex}
            progressInStep={progressInStep}
            theme={theme}
          />
        )}

        <div
          className={`mt-7 text-center font-bold leading-none text-slate-900 ${isHillsAndValleys
            ? "text-3xl sm:text-4xl"
            : "text-4xl tabular-nums sm:text-5xl"
            }`}
        >
          {footerText}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        {isReadyToStart && (
          <div className="col-span-2 text-center text-sm text-slate-600">
            Click or tap{" "}
            <span className="font-semibold text-slate-800">Start</span> below
            to begin practicing {def.title}.
          </div>
        )}

        <button
          type="button"
          onClick={reset}
          className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
        >
          Reset
        </button>

        {isReadyToStart ? (
          <button
            type="button"
            onClick={start}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white"
          >
            Start
          </button>
        ) : isRunning ? (
          <button
            type="button"
            onClick={pause}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white"
          >
            Pause
          </button>
        ) : (
          <button
            type="button"
            onClick={resume}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white"
          >
            Resume
          </button>
        )}
      </div>
    </>
  );

  return (
    <>
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        {renderCoachSkillContent()}
      </div>

      {isCoachExpanded && typeof document !== "undefined"
        ? createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/70 p-3 sm:p-5">
            <div className="flex h-full max-h-[900px] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
                {renderCoachSkillContent()}
              </div>
            </div>
          </div>,
          document.body
        )
        : null}
    </>
  );
});

export default CoachSkillWidget;
